import { confirmDelivery } from "../../services/apis/dispense/confirmDelivery";
import { removePatientById } from "../../services/slices/dispense/patientPendingList";
import { removeTodayDispenePatientById } from "../../services/slices/dispense/patientForRouteList";
import { getPharmacyOrderDeliveryTime } from "./helper";
import { removeFlexiDeliverById } from "../../services/slices/dispense/flexiDeliver";
import { reportUnexpectedError } from "./reportError";
import { isRxAlreadyDelivered, withoutDeliveredRxIds } from "./deliveredRxDisplay";
import { resolveDoseGapBlock } from "./doseGap";
import { requestDoseGapOverride } from "./doseGapPrompt";

const getSelectedDeliveryRxData = (rxData = [], selectedDeliverRxIds = []) => {
    if (!Array.isArray(rxData)) return [];
    if (!selectedDeliverRxIds?.length) return rxData;

    const selectedIdSet = new Set(selectedDeliverRxIds.map((id) => String(id)));
    return rxData.filter((rx) => selectedIdSet.has(String(rx?.id)));
};

const isPrescriptionSignaturePending = (rx) => {
    const signatureData = rx?.pharmacyWinrxRxEs ?? rx?.pharmacy_winrx_rx_es;
    return signatureData?.signature_required === 1 && signatureData?.signature_complete === 0;
};

const isFentanylRx = (rx) => rx?.is_din_matched?.is_fentanyl === 1;

// -----------------------------Pending and today dispense delivery-------------------------------

export const onhandleConfirmDelivery = (patientData,
    index,
    pharmacyId,
    selectedPharmacistID,
    patientSignature, 
    pharmacistSignature,
    cordinate,
    comment,
    dispatch,
    setLoading,
    setOpenIndex,
    setShowErrorToast,
    setShowSuccessToast,
    pharmacyInspectionMode,
    isRefused,
    covid19,
    type,
    setIsRefused,
    setSignatureErrors,
    validateReturnedPatchesResponse,
    isTransferRequest,
    setIsTransferRequest,
    consultation,
    newRxCounsellingValues,
    selectedDoses,
    flexiProofImages = {},
    copayPaymentOptions = {},
    patientSerchData = null,
    isPatientVerified = true,
    requestedDeliverRxIds = [],
    deliveryStockDisposition = null,
    deliveryStockSignature = null
) => {

    // Already delivered Rxs must never be sent to confirm-delivery again.
    const allRxData = patientData?.patients_win_rx_data || [];
    const hasDeliveredRx = allRxData.some(isRxAlreadyDelivered);
    let selectedDeliverRxIds = withoutDeliveredRxIds(requestedDeliverRxIds, allRxData);
    // With no ids the backend updates every Rx for today, delivered ones included,
    // so send the remaining deliverable ids explicitly instead.
    if (!requestedDeliverRxIds?.length && hasDeliveredRx && type !== 'flexi_deliver') {
        selectedDeliverRxIds = allRxData
            .filter((rx) => !isRxAlreadyDelivered(rx))
            .map((rx) => rx?.id)
            .filter((id) => id != null);
    }
    if ((requestedDeliverRxIds?.length || hasDeliveredRx) && selectedDeliverRxIds.length === 0) {
        if (typeof setLoading === 'function') setLoading(false);
        alert('The selected prescriptions are already delivered.');
        return;
    }

    const front_delivery_time = getPharmacyOrderDeliveryTime();
    const params = {
        route: patientData.route,
        pharmacy_id: pharmacyId,
        setLoading: setLoading,
    }

    const flexiDeliveryIDs = getFlexiDeliveryPatientIds(patientData?.patients_win_rx_data);
    const flexiDeliveryIDsToSend = selectedDeliverRxIds?.length > 0
        ? (flexiDeliveryIDs?.filter(id => selectedDeliverRxIds.includes(id)) || [])
        : flexiDeliveryIDs;
    const newRxCounsellingValuesToSend = selectedDeliverRxIds?.length > 0
        ? (newRxCounsellingValues?.filter((value) => value?.rxid != null && selectedDeliverRxIds.includes(value.rxid)) || [])
        : newRxCounsellingValues;
    const paymentOptionsToSend = selectedDeliverRxIds?.length > 0
        ? Object.entries(copayPaymentOptions || {}).filter(([winrxDataId]) => selectedDeliverRxIds.includes(Number(winrxDataId)) || selectedDeliverRxIds.includes(winrxDataId)).map(([, v]) => v).filter(option => option !== null)
        : Object.values(copayPaymentOptions || {}).filter(option => option !== null);
    const flexiProofImagesToSend = selectedDeliverRxIds?.length > 0
        ? Object.fromEntries(Object.entries(flexiProofImages || {}).filter(([, v]) => v?.id != null && selectedDeliverRxIds.includes(v.id)))
        : flexiProofImages;

    const requesteData = {
        pharmacy_id: pharmacyId,
        pharmacist_id: selectedPharmacistID,
        patient_sign: [...patientSignature],
        pharmacist_sign: pharmacistSignature,
        delivery_latitude: cordinate?.latitude,
        delivery_longitude: cordinate?.longitude,
        patient_id: patientData?.id,
        comment: comment,
        dispatch: dispatch,
        params: params,
        setLoading: setLoading,
        setOpenIndex: setOpenIndex,
        setShowErrorToast: setShowErrorToast,
        setShowSuccessToast: setShowSuccessToast,
        type: type,
        is_refused: isRefused ? 1 : 0,
        covid19_exemptions: covid19 ? 1 : 0,
        is_return: validateReturnedPatchesResponse.isReturn,
        isTransferRequest: isTransferRequest,
        setIsTransferRequest: setIsTransferRequest,
        front_delivery_time: front_delivery_time,
        consultation: consultation,
        new_rx_counselling_values: newRxCounsellingValuesToSend,
        frequency_dose_id: Object?.values(selectedDoses)?.flat()?.map(dose => dose?.dose_id),
        flexi_rx_refill_id: flexiDeliveryIDsToSend,
        hasFlexOnly: type,
        flexi_proof_images: flexiProofImagesToSend,
        payment_options: paymentOptionsToSend,
        is_patient_verified: Boolean(isPatientVerified),
        ...(type === 'patient_search_list' && patientSerchData ? { patientSerchData } : {}),
        ...(selectedDeliverRxIds?.length > 0 ? { deliver_rx_ids: selectedDeliverRxIds } : type === 'flexi_deliver' && flexiDeliveryIDsToSend?.length > 0 ? { deliver_rx_ids: flexiDeliveryIDsToSend } : {}),
        ...(deliveryStockDisposition ? { delivery_stock_disposition: deliveryStockDisposition } : {}),
        ...(deliveryStockSignature ? { delivery_stock_signature: deliveryStockSignature } : {})
    }

    // Every refusal below either highlights a field far above the Continue button
    // or, in one case, does nothing visible at all. Tapping Continue and seeing
    // nothing move is indistinguishable from a broken button, so each one now
    // also says what is wrong where the driver is actually looking, and leaves
    // the button usable again.
    const refuse = (message) => {
        if (typeof setShowErrorToast === 'function') {
            setShowErrorToast(message);
            setTimeout(() => setShowErrorToast(false), 4000);
        }
        if (typeof setLoading === 'function') setLoading(false);
    };

    try {
        const deliveryRuleRxData = getSelectedDeliveryRxData(patientData?.patients_win_rx_data, selectedDeliverRxIds);
        const hasRxWitness = deliveryRuleRxData.some(patient => patient.rx_witness === "Y");
        const fentanylPatchesToCheck = deliveryRuleRxData.filter(isFentanylRx);
        const investageRx = deliveryRuleRxData.some(isPrescriptionSignaturePending);

        if (pharmacyId && patientData?.id) {
            const signaturesRequired = pharmacyInspectionMode === 1 && ((isRefused ? null : patientSignature[0] == null))

            const checkReturnedPatches = () => {
                if (validateReturnedPatchesResponse?.winrx_data_id?.length !== fentanylPatchesToCheck?.length) {
                    refuse('Answer the returned patch question for every patch before continuing.');
                } else {
                    handleSignature(signaturesRequired);
                }
            };

            if (hasRxWitness) {
                if (patientSignature[0] == null && !covid19 && !isRefused) {
                    setSignatureErrors(prevState => ({ ...prevState, patientSign: '1px solid red', pharmacistID: selectedPharmacistID ? '' : '1px solid red' }))
                    refuse(selectedPharmacistID
                        ? 'This prescription is witnessed, so the patient signature is required.'
                        : 'This prescription is witnessed. A patient signature and a pharmacist are required.');
                }
                else if (fentanylPatchesToCheck?.length) {
                    checkReturnedPatches();
                }
                else {
                    handleSignature(signaturesRequired);
                    setSignatureErrors(prevState => ({ ...prevState, patientSign: null }))
                }
            }
            else if (fentanylPatchesToCheck?.length) {
                checkReturnedPatches();
            } else {
                handleSignature(signaturesRequired)
            }
        }

        function handleSignature(isSignatureRequired) {

            const patientSignError = ((isSignatureRequired || investageRx || isTransferRequest || newRxCounsellingValuesToSend?.length > 0) && patientSignature[0] === null);
            const hasNewRxValues = newRxCounsellingValuesToSend?.every((value) => value?.dropdownValue?.value);
            if (!hasNewRxValues) {
                // Nothing on screen moves for this one: no field is highlighted
                // and no request is sent, so without a message Continue simply
                // looks broken.
                refuse('Select a counselling option for each new prescription before continuing.');
                return
            }
            else if (patientSignError && !isRefused) {
                setSignatureErrors(prevState => ({ ...prevState, patientSign: '1px solid red' }));
                refuse('Patient signature is required before continuing.');
            }
            else if (!selectedPharmacistID) {
                setSignatureErrors(prevState => ({ ...prevState, pharmacistID: '1px solid red' }));
                refuse('Select the pharmacist before continuing.');
            }

            else {
                setSignatureErrors(prevState => ({ ...prevState, patientSign: null, pharmacistSign: null, pharmacistID: null }));
                setIsRefused(false);

                // The existing dispatch, unchanged, wrapped so it can wait for
                // the early-dose answer.
                const finalizeDelivery = () => {
                    if (type === 'patient_search_list') {
                        setTimeout(() => setShowSuccessToast(false), 2000);
                        setLoading(false);
                        dispatch(confirmDelivery(requesteData));
                    } else {
                        setShowSuccessToast(true);
                        setOpenIndex(null);
                        if (type === 'today_dispense') {
                            dispatch(removeTodayDispenePatientById(patientData?.id));
                        } else if (type === 'flexi_deliver') {
                            dispatch(removeFlexiDeliverById(patientData?.id));
                        } else {
                            dispatch(removePatientById(patientData?.id));
                        }
                        setTimeout(() => {
                            const nextIndex = index + 1;
                            const nextPatientCard = document.getElementById(`patientCard-${nextIndex}`);
                            if (nextPatientCard) {
                                nextPatientCard.scrollIntoView({ behavior: "smooth", block: "center" });
                            }
                        }, 100);
                        setTimeout(() => setShowSuccessToast(false), 2000);
                        setLoading(false);
                        dispatch(confirmDelivery(requesteData));
                    }
                };

                // A dose the backend flagged as not due yet is confirmed before
                // anything is sent. With no flagged dose this calls
                // finalizeDelivery straight away, exactly as before.
                withDoseGapConfirmation(selectedDoses, requesteData, finalizeDelivery, setLoading, setShowErrorToast);
            }
        }

    } catch (error) {
        reportUnexpectedError('Delivery confirmation preparation failed', error, {
            patientId: patientData?.id,
            type,
        });
        if (typeof setLoading === 'function') {
            setLoading(false);
        }
        if (typeof setShowErrorToast === 'function') {
            setShowErrorToast(true);
            setTimeout(() => setShowErrorToast(false), 3000);
        }
    }
};

/**
 * Run a delivery, asking the driver to confirm first when the backend flagged a
 * selected dose as not due yet.
 *
 * The rule and the wording both live server-side; this only relays the prompt
 * and attaches the answer. Identical to the Driver app, deliberately: one shape
 * for both apps means one place for this to go wrong.
 */
const withDoseGapConfirmation = (selectedDoses, requesteData, finalizeDelivery, setLoading, setShowErrorToast) => {
    const prompt = resolveDoseGapBlock(selectedDoses);

    if (!prompt) {
        finalizeDelivery();
        return;
    }

    requestDoseGapOverride(prompt).then((result) => {
        if (!result?.confirmed) {
            // Declined without the driver ever seeing the dialog stops the
            // delivery with nothing on screen, which reads as a dead button.
            // Say why instead.
            if (!result?.shown && typeof setShowErrorToast === 'function') {
                setShowErrorToast(prompt.message || 'This dose is not due yet.');
                setTimeout(() => setShowErrorToast(false), 4000);
            }
            if (typeof setLoading === 'function') setLoading(false);
            return;
        }

        requesteData.gap_override_confirmed = true;
        requesteData.gap_override_reason = result.reason;
        finalizeDelivery();
    });
};

export const getFlexiDeliveryPatientIds = (patientData) => {
    return patientData?.filter(patient => patient.flexi_delivery)
        ?.map(patient => patient.id) || [];
}















